<!-- Color.vue -->
<template>
  <div class="color-wrapper">
    <!-- Color button to toggle dropdown -->
    <button
      class="color-action"
      :style="{ background: currentColor || '#000000' }"
      @click="toggleDropdown"
    />

    <Popover
      ref="op"

      :pt="{
        root: { class: 'color-popover' },
        content: { class: 'color-popover-content' },
      }"
    >
      <div class="flex flex-col gap-2 p-3">
        <div class="flex flex-col gap-2">
          <div class="color-grid grid grid-cols-8 gap-2">
            <button
              v-for="color in presetColors"
              :key="color"
              class="color-option h-6 w-6 rounded-full border border-gray-200 shadow-sm hover:scale-110 transition-transform"
              :style="{ background: color }"
              @click="setColor(color)"
            />
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-gray-700">Recently Used</span>
          <div class="flex gap-2">
            <button
              v-for="color in colors"
              :key="color"
              class="color-option h-6 w-6 rounded-full border border-gray-200 shadow-sm hover:scale-110 transition-transform"
              :style="{ background: color }"
              @click="setColor(color)"
            />
          </div>
        </div>

        <div class="more-colors flex items-center gap-2 border-t border-gray-200 pt-3">
          <input
            type="color"
            :value="currentColor || '#000000'"
            class="color-picker h-8 w-8 cursor-pointer rounded border-0"
            @change="updateColor($event)"
          >
          <span class="text-sm text-gray-600">Custom Color</span>
        </div>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { computed, defineModel, ref } from 'vue'
import Popover from 'primevue/popover'
import { useTextEditorStore } from '@/store/texteditor'

const editor = defineModel('editor')

// List of preset colors (you can adjust these as needed)
const presetColors = ref([
  '#000000', // Black
  '#545454', // Charcoal
  '#0D47A1', // Deep Blue
  '#1976D2', // Standard Blue
  '#2E7D32', // Forest Green
  '#7B1FA2', // Purple
  '#C62828', // Dark Red
  '#00796B', // Teal
])

// State to manage dropdown visibility
const isDropdownOpen = ref(false)

// Compute the current color from the editor
const currentColor = computed(() => {
  return editor.value?.getAttributes('textStyle')?.color || '#000000'
})
const op = ref()

const { colors } = storeToRefs(useTextEditorStore())

// Toggle dropdown visibility
const toggleDropdown = (event) => {
  op.value.toggle(event)
}
// Set color when a preset color is clicked
const setColor = (color: string) => {
  editor.value?.chain().focus().setColor(color).run()
  isDropdownOpen.value = false // Close dropdown after selection
  op.value.hide()
}

// Update color from the full color picker
const updateColor = (event: Event) => {
  const target = event.target as HTMLInputElement
  editor.value?.chain().focus().setColor(target.value).run()
  isDropdownOpen.value = false // Close dropdown after selection
  useTextEditorStore().addColor(target.value)
}
</script>

<style scoped>
.color-wrapper {
  position: relative;
  display: inline-block;
}

.color-action {
  width: 34px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  margin: 0 2px;
  border: 1px solid #ccc;
}

.color-action.is-active {
  border: 2px solid #000;
}

:deep(.color-popover) {
  min-width: 200px;
}

:deep(.color-popover-content) {
  padding: 1rem;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  /* Adjust based on number of colors */
  gap: 4px;
  margin-bottom: 8px;
}

.color-option {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid #ccc;
}

.color-option:hover {
  border: 1px solid #000;
}

.more-colors {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-picker {
  width: 24px;
  height: 24px;
  border: none;
  cursor: pointer;
  padding: 0;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}

span {
  font-size: 12px;
  color: #333;
  cursor: pointer;
}
</style>
